import React, { useEffect } from "react";
import {
  FiDollarSign,
  FiShoppingBag,
  FiTag,
  FiStar,
  FiExternalLink,
  FiPlus,
  FiList,
  FiBarChart,
  FiMessageCircle,
} from "react-icons/fi";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import "../../Styles/Seller/dashboard.css";
import { useNavigate } from "react-router-dom";

const performance = [
  { label: "Sales Target", value: 75, color: "#22c55e" },
  { label: "Customer Satisfaction", value: 92, color: "#2563eb" },
  { label: "Order Fulfillment", value: 88, color: "#f59e0b" },
];

const fmt = (price) => {
  if (typeof price === "number") {
    return `AED ${price.toLocaleString("en-IN")}`;
  }
  return `AED ${price}`;
};

export default function Dashboard({
  totalActiveProductCount,
  totalRevenue,
  RecentOrders,
  lowStockProducts,
}) {
  const navigate = useNavigate();

  // Prepare data for pie chart
  const pieChartData = [
    { name: "Total Revenue", value: totalRevenue?.total_revenue || 0, color: "#22c55e" },
    { name: "Total Orders", value: totalRevenue?.order_count || 0, color: "#3b82f6" },
    { name: "Active Products", value: totalActiveProductCount || 0, color: "#f59e0b" },
    { name: "Customer Rating", value: 4.8, color: "#8b5cf6" },
  ];

  const stats = [
    {
      title: "Total Revenue",
      value: totalRevenue?.total_revenue,
      change: "+12.5%",
      icon: "AED",
    },
    {
      title: "Total Orders",
      value: totalRevenue?.order_count,
      change: "+8.2%",
      icon: <FiShoppingBag />,
    },
    {
      title: "Active Products",
      value: totalActiveProductCount,
      change: "-2.1%",
      icon: <FiTag />,
    },
    {
      title: "Customer Rating",
      value: "4.8",
      change: "+0.3",
      icon: <FiStar />,
    },
  ];

  useEffect(() => {
    const animateElements = document.querySelectorAll("[data-animate]");
    const cardElements = document.querySelectorAll("[data-card-anim]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      },
    );

    animateElements.forEach((el) => observer.observe(el));
    cardElements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, []);

  const openStockManagement = () => navigate("/seller/product");

  const products = Array.isArray(lowStockProducts?.products)
    ? lowStockProducts.products
    : [];
  const threshold = Number(lowStockProducts?.threshold ?? 0);

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="pie-chart-tooltip">
          <div className="tooltip-label">{data.name}</div>
          <div className="tooltip-value">
            {data.name === "Customer Rating" 
              ? `${data.value} ★` 
              : data.name === "Total Revenue"
              ? `AED ${data.value.toLocaleString("en-IN")}`
              : data.value.toLocaleString("en-IN")}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="db-page">
        <div className="db-header" data-animate>
          <h1>Dashboard Overview</h1>
          <button className="dashboard-export-btn">
            <FiExternalLink /> Export Report
          </button>
        </div>

        {/* Stats Cards Row */}
        <div className="db-stats">
          {stats.map((s, i) => (
            <div className="stat-card" key={i} data-card-anim="fade-up">
              <div className="stat-left">
                <div className="stat-title">{s.title}</div>
                <div className="stat-value">
                  {s.title === "Total Revenue" 
                    ? fmt(s.value)
                    : s.title === "Customer Rating"
                    ? s.value
                    : s.value}
                </div>
                <div
                  className={`stat-change ${
                    s.change.startsWith("-") ? "down" : "up"
                  }`}
                >
                  {s.change.startsWith("-") ? "↘" : "↗"} {s.change}
                </div>
              </div>
              <div className="stat-icon">{s.icon}</div>
            </div>
          ))}
        </div>

        {/* Middle Section: Pie Chart (Left) + Quick Actions (Right) */}
        <div className="dashboard-middle-section">
          {/* Left Side - Pie Chart */}
          <div className="pie-chart-card" data-card-anim="fade-right">
            <h3>Analytics Overview</h3>
            <div className="pie-chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => 
                      `${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={true}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={50}
                    formatter={(value, entry) => {
                      const data = entry.payload;
                      if (data.name === "Customer Rating") {
                        return `${data.name}: ${data.value} ★`;
                      }
                      if (data.name === "Total Revenue") {
                        return `${data.name}: AED ${data.value.toLocaleString("en-IN")}`;
                      }
                      return `${data.name}: ${data.value.toLocaleString("en-IN")}`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right Side - Quick Actions */}
          <div className="quick-actions-card" data-card-anim="fade-left">
            <h3>Quick Actions</h3>
            <div className="actions-list">
              <button className="action-btn" onClick={openStockManagement} data-animate>
                <FiPlus /> Update Stock
              </button>
              <button
                className="action-btn"
                onClick={() => {
                  navigate("/seller/order");
                }}
                data-animate
              >
                <FiList /> Process Orders
              </button>
              <button
                className="action-btn"
                onClick={() => {
                  navigate("/seller/analytics");
                }}
                data-animate
              >
                <FiBarChart /> View Analytics
              </button>
              <button
                className="action-btn"
                onClick={() => {
                  navigate("/contact");
                }}
                data-animate
              >
                <FiMessageCircle /> Customer Support
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section: Recent Orders */}
        <div className="recent-orders-section">
          <div className="recent-orders-card">
            <div className="card-title-row">
              <h3>Recent Orders</h3>
              <a className="view-all">View All</a>
            </div>

            <div className="orders-table" role="table">
              <div className="orders-row header">
                <div className="col col-id">Order ID</div>
                <div className="col col-customer">Customer</div>
                <div className="col col-product">Product</div>
                <div className="col col-amount">Amount</div>
                <div className="col col-status">Status</div>
                <div className="col col-date">Date</div>
              </div>

              {RecentOrders &&
                RecentOrders.map((o) => (
                  <div className="orders-row" key={o.id}>
                    <div className="col col-id">#ORD-{o.last4digits}</div>
                    <div className="col col-customer">{o.username}</div>
                    <div className="col col-product">{o.product}</div>
                    <div className="col col-amount">{o.total_amount}</div>
                    <div className="col col-status">
                      <span
                        className={`dashboard-ord-status-dot ${o.status?.toLowerCase()}`}
                      ></span>
                      {o.status}
                    </div>
                    <div className="col col-date">
                      {o.last_status_change_at
                        ? new Date(o.last_status_change_at).toLocaleDateString(
                            "en-IN",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )
                        : "-"}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Alerts and Performance Summary Section */}
      <section className="ip-section">
        <div className="ip-container">
          <div className="ip-left card" data-animate>
            <h3 className="card-title">Inventory Alerts</h3>

            {!lowStockProducts ? (
              <div className="inventory-empty">Loading inventory...</div>
            ) : !products.length ? (
              <div className="inventory-empty">No low-stock products.</div>
            ) : (
              products.map((p) => {
                const stock = Number(p.stock_quantity ?? 0);
                const isLow = threshold ? stock < threshold : stock <= 0;
                const firstImage = p.image_url;
                const price = fmt(p.price);

                return (
                  <div className="inventory-row" key={p.id}>
                    <div className="inventory-thumb">
                      {firstImage ? (
                        <img
                          src={firstImage}
                          style={{ width: "100px" }}
                          alt={p.name || "product"}
                        />
                      ) : (
                        <div className="no-thumb">No image</div>
                      )}
                    </div>

                    <div className="inventory-text">
                      <div className="inventory-name">{p.name}</div>
                      <div className="inventory-stock">
                        {stock} unit{stock !== 1 ? "s" : ""}{" "}
                        {threshold ? `(Threshold: ${threshold})` : ""}
                      </div>
                      <div className="inventory-meta">
                        {price}
                        {p.discount ? <span> • Disc {p.discount}%</span> : null}
                      </div>
                    </div>

                    <div className="inventory-badge">
                      <span className={`badge ${isLow ? "low" : "ok"}`}>
                        {isLow ? "Low Stock" : "OK"}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="ip-right card" data-card-anim="zoom-in">
            <h3 className="card-title">Performance Summary</h3>

            <div className="performance-list">
              {performance.map((p) => (
                <div className="perf-row" key={p.label} data-animate>
                  <div className="perf-top">
                    <div className="perf-label">{p.label}</div>
                    <div className="perf-value">{p.value}%</div>
                  </div>

                  <div className="perf-bar-outer" aria-hidden>
                    <div
                      className="perf-bar-inner"
                      style={{
                        width: `${p.value}%`,
                        backgroundColor: p.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
